import { HoverCard, Tooltip } from '@mantine/core'
import { AlertTriangle } from 'lucide-react'

import type { ReviewStatus } from 'src/lib/documentIntelligence'
import {
  formatExtractedDetailValue,
  formatExtractedRecordDate,
  formatExtractedRowValue,
  valuesMismatch,
} from 'src/lib/documentIntelligence'

import { AiConfidencePill } from '../AiConfidencePill'

// A label/value pair for any finding field beyond date/location/description,
// shown below the description in the hover. Assembled on the backend.
export interface AiFindingDetail {
  label: string
  value: string
}

// One individual finding behind a count row (e.g. a single MVR accident),
// surfaced as a hover detail. Assembled on the backend (AiFindingRecord).
export interface AiFindingRecord {
  date?: string | null
  location?: string | null
  description?: string | null
  details?: AiFindingDetail[] | null
}

// A display-ready row in the "AI Extracted" popover. Shape mirrors the backend
// AiExtractedRow GraphQL type. Scalar rows carry an empty `records`; finding
// count rows carry one record per individual finding for the hover tooltip.
export interface AiExtractedRow {
  field: string
  label: string
  value: string
  confidence: number
  reviewStatus?: ReviewStatus | null
  records?: AiFindingRecord[] | null
}

interface ExtractedFieldRowProps {
  field: AiExtractedRow
  // The value of record in the application/document for this field, shown in the
  // pill tooltip for comparison against the AI extraction. null = not set.
  actualValue?: string | null
}

// The finding detail shown on hover. Each finding is a block with a bold
// heading and the AI-extracted description below (descriptions arrive ALL CAPS
// off the document, so lowercase with a first-letter cap). The finding type is
// intentionally NOT repeated here — it's already the row label the recruiter
// hovered — so the heading leads with the location, falling back to the date
// when there's no location (suspensions, license actions, etc.). Every other
// extracted field follows the description as a label/value line, so a finding
// that survived per-field gating on, say, an ACD code still shows it. Colors
// match the original (dark) tooltip; it just lives in a scrollable HoverCard now.
const RecordsList = ({ records }: { records: AiFindingRecord[] }) => (
  <div className="flex flex-col gap-1.5">
    {records.map((record, i) => {
      const date = formatExtractedRecordDate(record.date)
      const heading = record.location || date
      const details = record.details ?? []
      // Skip records with nothing to show — when every field was dropped for low
      // confidence, render nothing rather than an empty block.
      if (!heading && !record.description && details.length === 0) return null
      return (
        <div key={i} className="flex flex-col">
          {heading && <span className="text-xs font-semibold">{heading}</span>}
          {record.location && date && <span className="text-xs">{date}</span>}
          {record.description && (
            <span className="text-xs lowercase text-doubleNickel-gray-300 first-letter:uppercase">
              {record.description}
            </span>
          )}
          {details.map((detail) => (
            <span
              key={detail.label}
              className="text-xs text-doubleNickel-gray-300"
            >
              <span className="font-medium">{detail.label}:</span>{' '}
              {formatExtractedDetailValue(detail.value)}
            </span>
          ))}
        </div>
      )
    })}
  </div>
)

// A single row in the "AI Extracted" popover: label (left), extracted value, and
// the confidence pill (right). The pill color-codes the review outcome and its
// tooltip shows what's actually in the application for comparison. When the AI
// value differs from the application, an amber warning icon flags the row. Rows
// backed by individual findings (e.g. MVR accident counts) reveal the per-finding
// detail in a HoverCard — NOT a tooltip — so a long list (13+ violations) stays
// open when the recruiter moves the mouse onto it and scrolls inside it, matching
// MvrFindingsPill's "max-h-80 overflow-y-auto" scroll style.
const ExtractedFieldRow = ({ field, actualValue }: ExtractedFieldRowProps) => {
  // Backend sends raw values (enum / ISO); format for display here, and compare
  // the formatted value against the (formatted) value of record.
  const displayValue = formatExtractedRowValue(field.field, field.value)
  const mismatch = valuesMismatch(field.field, displayValue, actualValue)
  const records = field.records ?? []
  const hasRecords = records.length > 0

  const rowClasses =
    'flex items-center justify-between gap-3 border-b border-doubleNickel-gray-100 py-1.5 last:border-b-0'

  const rowBody = (
    <>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-doubleNickel-gray-400">
        {field.label}
      </span>
      <div className="flex items-center gap-2">
        {mismatch && (
          <Tooltip
            label={`Differs from application: ${actualValue}`}
            withArrow
            className="text-xs"
          >
            <AlertTriangle
              size={12}
              className="shrink-0 text-doubleNickel-warning-500"
            />
          </Tooltip>
        )}
        <span className="text-right text-xs font-normal text-doubleNickel-gray-800">
          {displayValue}
        </span>
        <AiConfidencePill
          confidence={field.confidence}
          status={field.reviewStatus}
          tooltipLabel="AI extracted"
        />
      </div>
    </>
  )

  if (!hasRecords) return <div className={rowClasses}>{rowBody}</div>

  return (
    <HoverCard
      width={280}
      position="left"
      offset={6}
      shadow="md"
      openDelay={100}
      closeDelay={150}
    >
      <HoverCard.Target>
        <div className={`${rowClasses} cursor-help`}>{rowBody}</div>
      </HoverCard.Target>
      {/* Dark dropdown to keep the original tooltip's color; capped + scrollable
          so a long finding list (13+ violations) doesn't run off the viewport. */}
      <HoverCard.Dropdown
        className="max-h-80 overflow-y-auto"
        style={{
          backgroundColor: 'var(--mantine-color-gray-9)',
          color: 'var(--mantine-color-white)',
          border: 'none',
        }}
      >
        <RecordsList records={records} />
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

export default ExtractedFieldRow
